import type { ActiveCartItem, ShoppingCategory } from '../types';

export const SHOPPING_CATEGORIES = [
    'General',
    'Meat',
    'Vegetables',
    'Fruits',
    'Dairy',
    'Spices',
    'Snacks',
    'BabyProducts',
    'Cleaning',
    'Canned',
    'Pantry',
    'Toiletries',
    'GeneralFood',
    'Frozen',
    'Bakery',
    'Beverages',
    'Other',
] as const satisfies readonly ShoppingCategory[];

export type PillarId =
    | 'manual'
    | 'meat'
    | 'vegetables'
    | 'fruits'
    | 'dairy'
    | 'spices'
    | 'snacks'
    | 'babyProducts'
    | 'cleaning'
    | 'toiletries'
    | 'canned'
    | 'pantry'
    | 'generalFood'
    | 'frozen'
    | 'bakery'
    | 'beverages'
    | 'general'
    | 'other'
    | 'others';

export const ALL_PILLARS: PillarId[] = [
    'manual',
    'meat',
    'vegetables',
    'fruits',
    'dairy',
    'spices',
    'snacks',
    'babyProducts',
    'canned',
    'pantry',
    'frozen',
    'bakery',
    'beverages',
    'generalFood',
    'cleaning',
    'toiletries',
    'general',
    'other',
    'others',
];

const CATEGORY_TO_PILLAR: Record<ShoppingCategory, PillarId> = {
    General: 'general',
    Meat: 'meat',
    Vegetables: 'vegetables',
    Fruits: 'fruits',
    Dairy: 'dairy',
    Spices: 'spices',
    Snacks: 'snacks',
    BabyProducts: 'babyProducts',
    Cleaning: 'cleaning',
    Canned: 'canned',
    Pantry: 'pantry',
    Toiletries: 'toiletries',
    GeneralFood: 'generalFood',
    Frozen: 'frozen',
    Bakery: 'bakery',
    Beverages: 'beverages',
    Other: 'other',
};

const PILLAR_TO_CATEGORY: Record<PillarId, ShoppingCategory | null> = {
    manual: null,
    meat: 'Meat',
    vegetables: 'Vegetables',
    fruits: 'Fruits',
    dairy: 'Dairy',
    spices: 'Spices',
    snacks: 'Snacks',
    babyProducts: 'BabyProducts',
    cleaning: 'Cleaning',
    toiletries: 'Toiletries',
    canned: 'Canned',
    pantry: 'Pantry',
    generalFood: 'GeneralFood',
    frozen: 'Frozen',
    bakery: 'Bakery',
    beverages: 'Beverages',
    general: 'General',
    other: 'Other',
    others: 'Other',
};

const SHOPPING_CATEGORY_SET = new Set<string>(SHOPPING_CATEGORIES);
const PILLAR_SET = new Set<string>(ALL_PILLARS);

export function isShoppingCategory(value: string): value is ShoppingCategory {
    return SHOPPING_CATEGORY_SET.has(value);
}

export function isPillarId(value: string): value is PillarId {
    return PILLAR_SET.has(value);
}

export function shoppingCategoryToPillar(category: string | undefined): PillarId {
    if (!category) return 'others';

    if (isShoppingCategory(category)) {
        return CATEGORY_TO_PILLAR[category];
    }

    const normalized = normalizeShoppingCategory(category);
    return CATEGORY_TO_PILLAR[normalized];
}

export function pillarToShoppingCategory(pillar: string): ShoppingCategory {
    if (isPillarId(pillar)) {
        return PILLAR_TO_CATEGORY[pillar] ?? 'Other';
    }
    return normalizeShoppingCategory(pillar);
}

export function normalizeShoppingCategory(catStr: string | undefined): ShoppingCategory {
    if (!catStr) return 'General';

    const lower = catStr.toLowerCase().trim();

    const matched = SHOPPING_CATEGORIES.find((c) => c.toLowerCase() === lower);
    if (matched) return matched;

    if (isPillarId(lower)) {
        return pillarToShoppingCategory(lower);
    }

    if (lower === 'בשר' || lower === 'meat') return 'Meat';
    if (lower === 'מוצרי חלב' || lower === 'גבינה' || lower === 'גבינות' || lower === 'חלב' || lower === 'dairy') return 'Dairy';
    if (lower === 'ירקות' || lower === 'vegetables') return 'Vegetables';
    if (lower === 'פירות' || lower === 'fruits') return 'Fruits';
    if (lower === 'תבלינים' || lower === 'תבלין' || lower === 'spices' || lower === 'spice') return 'Spices';
    if (lower === 'חטיפים' || lower === 'חטיף' || lower === 'snacks' || lower === 'snack') return 'Snacks';
    if (lower === 'לתינוק' || lower === 'תינוק' || lower === 'תינוקות' || lower === 'מוצרי תינוק' || lower === 'מוצרי תינוקות' || lower === 'babyproducts' || lower === 'baby products' || lower === 'baby') return 'BabyProducts';
    if (lower === 'ניקיון' || lower === 'חומרי ניקוי' || lower === 'cleaning') return 'Cleaning';
    if (lower === 'טואלטיקה' || lower === 'toiletries') return 'Toiletries';
    if (lower === 'שימורים' || lower === 'canned' || lower === 'cannedgoods' || lower === 'canned goods') return 'Canned';
    if (lower === 'מזווה' || lower === 'pantry') return 'Pantry';
    if (lower === 'מצרכים כלליים' || lower === 'מזון כללי' || lower === 'פריטים שונים' || lower === 'כללי' || lower === 'general' || lower === 'generalfood' || lower === 'general food') return 'GeneralFood';
    if (lower === 'קפואים' || lower === 'frozen') return 'Frozen';
    if (lower === 'מאפים' || lower === 'bakery') return 'Bakery';
    if (lower === 'משקאות' || lower === 'beverages') return 'Beverages';
    if (lower === 'אחר' || lower === 'other' || lower === 'others') return 'Other';

    return 'General';
}

export function detectCategoryFromName(name: string, isMeat: boolean): ShoppingCategory {
    if (isMeat) return 'Meat';

    const clean = name.toLowerCase();

    if (
        clean.includes('פשתן') ||
        clean.includes('flax') ||
        clean.includes('flaxseed') ||
        clean.includes('linseed')
    ) return 'Spices';

    if (clean.includes('חלב') || clean.includes('גבינ') || clean.includes('cheese') || clean.includes('milk') || clean.includes('שמנת') || clean.includes('cream') || clean.includes('חמאה') || clean.includes('butter') || clean.includes('yogurt') || clean.includes('יוגורט')) return 'Dairy';

    if (clean.includes('תבלין') || clean.includes('spice') || clean.includes('כמון') || clean.includes('cumin') || clean.includes('פפריקה') || clean.includes('paprika') || clean.includes('קינמון') || clean.includes('cinnamon') || clean.includes('כורכום') || clean.includes('turmeric') || clean.includes('זעתר') || clean.includes('שומשום') || clean.includes('sesame') || clean.includes('אורגנו') || clean.includes('oregano') || clean.includes('בזיליקום') || clean.includes('basil')) return 'Spices';

    if (clean.includes('חטיף') || clean.includes('snack') || clean.includes('ביסלי') || clean.includes('במבה') || clean.includes('פופקורן') || clean.includes('popcorn') || clean.includes('chips') || clean.includes('crisp') || clean.includes('פיצוח') || clean.includes('אגוז') || clean.includes('nut') || clean.includes('שקד') || clean.includes('almond') || clean.includes('walnut') || clean.includes('פקאן') || clean.includes('pecan') || clean.includes('קשיו') || clean.includes('cashew')) return 'Snacks';

    if (clean.includes('תינוק') || clean.includes('baby') || clean.includes('חיתול') || clean.includes('diaper') || clean.includes('מוצץ') || clean.includes('pacifier') || clean.includes('formula') || clean.includes('תחליף חלב')) return 'BabyProducts';

    if (clean.includes('עגבני') || clean.includes('בצל') || clean.includes('שום') || clean.includes('מלפפון') || clean.includes('tomato') || clean.includes('onion') || clean.includes('garlic') || clean.includes('cucumber') || clean.includes('גזר') || clean.includes('carrot') || clean.includes('חסה') || clean.includes('lettuce')) return 'Vegetables';

    if (clean.includes('apple') || clean.includes('banana') || clean.includes('orange') || clean.includes('lemon') || clean.includes('lime') || clean.includes('תפוח') || clean.includes('בננה') || clean.includes('תפוז') || clean.includes('לימון')) return 'Fruits';

    if (clean.includes('בשר') || clean.includes('עוף') || clean.includes('דג')) return 'Meat';
    if (clean.includes('ירק') || clean.includes('סלט')) return 'Vegetables';
    if (clean.includes('פרי')) return 'Fruits';
    if (clean.includes('ניק')) return 'Cleaning';
    if (clean.includes('טואלט') || clean.includes('סבון') || clean.includes('שמפו')) return 'Toiletries';
    if (clean.includes('שימור')) return 'Canned';
    if (clean.includes('קמח') || clean.includes('flour') || clean.includes('סוכר') || clean.includes('sugar') || clean.includes('פסטה') || clean.includes('pasta') || clean.includes('אורז') || clean.includes('rice') || clean.includes('פירור') || clean.includes('breadcrumb') || clean.includes('panko')) return 'Pantry';
    if (clean.includes('שמן') || clean.includes('oil')) return 'Pantry';
    if (clean.includes('קפא') || clean.includes('קפוא')) return 'Frozen';
    if (clean.includes('מאפ') || clean.includes('לחם') || clean.includes('bread')) return 'Bakery';
    if (
        clean.includes('שתייה') || clean.includes('משקה') || clean.includes('משקא') || clean.includes('מיץ') ||
        clean.includes('juice') || clean.includes('soda') || clean.includes('coffee') || clean.includes('קפה') ||
        clean.includes('tea') || clean.includes('תה') || clean.includes('water') || clean.includes('מים') ||
        clean.includes('beer') || clean.includes('בירה') || clean.includes('wine') || clean.includes('יין') ||
        clean.includes('cola') || clean.includes('קולה') || clean.includes('drink') || clean.includes('beverage')
    ) return 'Beverages';

    if (clean.includes('pepper') && !clean.includes('bell')) return 'Spices';

    return 'General';
}

export function isManualOnlyItem(item: ActiveCartItem): boolean {
    return item.sources.length > 0 && item.sources.every((s) => s.originType === 'manual');
}

export const LIST_PILLAR_PREFIX = 'list:';

export type ListPillar = {
    id: string;
    name: string;
};

export function toListPillarId(cartId: string): string {
    return `${LIST_PILLAR_PREFIX}${cartId}`;
}

export function isListPillarId(value: string): boolean {
    return value.startsWith(LIST_PILLAR_PREFIX);
}

export function listPillarIdToCartId(pillarId: string): string {
    return pillarId.slice(LIST_PILLAR_PREFIX.length);
}

export function buildListPillars(
    shoppingCarts: ReadonlyArray<{ id: string; name: string; isMisc?: boolean; isDeleted?: boolean }>,
): ListPillar[] {
    return shoppingCarts
        .filter((cart) => !cart.isMisc && !cart.isDeleted)
        .map((cart) => ({ id: toListPillarId(cart.id), name: cart.name }));
}

export function buildActiveCartPillarIds(
    shoppingCarts: ReadonlyArray<{ id: string; name: string; isMisc?: boolean; isDeleted?: boolean }>,
): string[] {
    return [...ALL_PILLARS, ...buildListPillars(shoppingCarts).map((pillar) => pillar.id)];
}

export function mergePillarOrder(
    savedOrder: readonly string[] | undefined,
    shoppingCarts: ReadonlyArray<{ id: string; name: string; isMisc?: boolean; isDeleted?: boolean }>,
): string[] {
    const allIds = buildActiveCartPillarIds(shoppingCarts);
    const normalized = (savedOrder ?? []).filter((id) => allIds.includes(id));
    const missing = allIds.filter((id) => !normalized.includes(id));
    return [...normalized, ...missing];
}

export function categoryToPillarId(
    category: string | undefined,
    listPillars: readonly ListPillar[] = [],
): string {
    if (!category) return 'others';

    if (isListPillarId(category)) return category;

    const listMatch = listPillars.find(
        (pillar) => pillar.name.toLowerCase().trim() === category.toLowerCase().trim(),
    );
    if (listMatch) return listMatch.id;

    if (isPillarId(category)) return category;

    if (isShoppingCategory(category)) return shoppingCategoryToPillar(category);

    return shoppingCategoryToPillar(normalizeShoppingCategory(category));
}

export function getPillarDisplayName(
    pillarId: string,
    listPillars: readonly ListPillar[],
    t: (key: string) => string,
): string {
    if (isListPillarId(pillarId)) {
        const cartId = listPillarIdToCartId(pillarId);
        return listPillars.find((pillar) => pillar.id === pillarId || listPillarIdToCartId(pillar.id) === cartId)?.name ?? pillarId;
    }

    if (isPillarId(pillarId)) {
        const translated = t(pillarId);
        return translated !== pillarId ? translated : pillarId;
    }

    return pillarId;
}

export function resolveItemPillar(
    item: ActiveCartItem,
    categoryOverrides?: Record<string, string>,
    listPillars: readonly ListPillar[] = [],
): string {
    const normalizedName = item.name.toLowerCase().trim();
    const override = categoryOverrides?.[normalizedName];
    if (override) return categoryToPillarId(override, listPillars);

    if (item.category) return categoryToPillarId(item.category, listPillars);

    if (isManualOnlyItem(item)) return 'manual';

    return shoppingCategoryToPillar(detectCategoryFromName(item.name, item.isMeat));
}

export function normalizeCategoryAssignment(
    newCategory: string,
    listPillars: readonly ListPillar[] = [],
): string {
    if (isListPillarId(newCategory)) return newCategory;

    const listMatch = listPillars.find(
        (pillar) => pillar.id === newCategory || pillar.name.toLowerCase().trim() === newCategory.toLowerCase().trim(),
    );
    if (listMatch) return listMatch.id;

    if (isPillarId(newCategory)) return pillarToShoppingCategory(newCategory);

    return normalizeShoppingCategory(newCategory);
}
